import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, Upload, ArrowLeftRight, TrendingUp, Coins, Zap } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { totalPortfolioValue, mainAccountValue, futuresAccountValue, spotBalance, marginBalance, futuresBalance, spotUpnl, marginPositionsUpnl, futuresUnrealizedPnl } from '../../utils/portfolioData';
import { AllocationChart } from '../home/AllocationChart';
// Match earn balance from PortfolioOverview
const earnBalance = 3247.89;
const earnRewards = 42.15; // Weekly rewards
// Chart data for portfolio overview
const chartData = {
  '24H': Array.from({
    length: 24
  }, (_, i) => ({
    time: `${i}:00`,
    value: 48500 + Math.random() * 2000,
    timestamp: `${i}:00`
  })),
  '7D': Array.from({
    length: 7
  }, (_, i) => ({
    time: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][i],
    value: 46800 + Math.random() * 3500,
    timestamp: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][i]
  })),
  '30D': Array.from({
    length: 30
  }, (_, i) => ({
    time: `Day ${i + 1}`,
    value: 44500 + Math.random() * 6000,
    timestamp: `Day ${i + 1}`
  })),
  '1Y': Array.from({
    length: 12
  }, (_, i) => ({
    time: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][i],
    value: 34500 + Math.random() * 16000,
    timestamp: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][i]
  })),
  ALL: Array.from({
    length: 24
  }, (_, i) => ({
    time: `M${i + 1}`,
    value: 15000 + Math.random() * 35500,
    timestamp: `Month ${i + 1}`
  }))
};
const CustomTooltip = ({
  active,
  payload
}: any) => {
  if (!active || !payload || !payload.length) return null;
  const data = payload[0].payload;
  const value = payload[0].value;
  return <div style={{
    background: 'rgba(20, 22, 32, 0.65)',
    backdropFilter: 'blur(32px) saturate(180%)',
    WebkitBackdropFilter: 'blur(32px) saturate(180%)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    borderRadius: '28px',
    padding: '16px 20px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.08)'
  }} data-id="element-1704">
      <div style={{
      color: '#9CA3AF',
      fontSize: '11px',
      marginBottom: '8px',
      letterSpacing: '0.05em',
      fontWeight: '400',
      textTransform: 'uppercase'
    }} data-id="element-1705">
        {data.timestamp}
      </div>
      <div style={{
      color: '#FFFFFF',
      fontSize: '28px',
      fontWeight: '300',
      letterSpacing: '-0.02em'
    }} data-id="element-1706">
        $
        {value.toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      })}
      </div>
    </div>;
};
export function PortfolioAccountOverview() {
  const navigate = useNavigate();
  const [timeRange, setTimeRange] = useState<keyof typeof chartData>('7D');
  const ranges: Array<keyof typeof chartData> = ['24H', '7D', '30D', '1Y', 'ALL'];
  const data = useMemo(() => {
    return chartData[timeRange];
  }, [timeRange]);
  const changeData = useMemo(() => {
    if (data.length < 2) return {
      amount: 0,
      percent: 0
    };
    const firstValue = data[0].value;
    const lastValue = data[data.length - 1].value;
    const amount = lastValue - firstValue;
    const percent = amount / firstValue * 100;
    return {
      amount,
      percent
    };
  }, [data]);
  const accounts = [{
    id: 'main',
    name: 'Main Account',
    icon: <Coins size={20} data-id="element-1707" />,
    totalValue: mainAccountValue,
    subAccounts: [{
      name: 'Spot',
      balance: spotBalance,
      upnl: spotUpnl
    }, {
      name: 'Margin',
      balance: marginBalance,
      upnl: marginPositionsUpnl
    }],
    color: 'bg-blue-500/10 text-blue-400'
  }, {
    id: 'futures',
    name: 'Futures',
    icon: <TrendingUp size={20} data-id="element-1708" />,
    totalValue: futuresAccountValue,
    balance: futuresBalance,
    upnl: futuresUnrealizedPnl,
    color: 'bg-purple-500/10 text-purple-400'
  }, {
    id: 'earn',
    name: 'Earn',
    icon: <Zap size={20} data-id="element-1709" />,
    totalValue: earnBalance,
    balance: earnBalance,
    rewards: earnRewards,
    color: 'bg-green-500/10 text-green-400'
  }];
  const totalChange = spotUpnl + marginPositionsUpnl + futuresUnrealizedPnl;
  const totalChangePercent = totalChange / totalPortfolioValue * 100;
  const handleAccountClick = (accountId: string) => {
    navigate(`/portfolio?account=${accountId}`);
    window.scrollTo(0, 0);
  };
  return <div className="space-y-6" data-id="element-1710">
      {/* Total Portfolio Value Card with Chart */}
      <div className="glass-border rounded-[20px] p-6" data-id="element-1711">
        {/* Header with Value and Timeframe Selector */}
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6" data-id="element-1712">
          <div data-id="element-1713">
            <div className="text-sm text-text-secondary mb-1 font-label uppercase" data-id="element-1714">
              Total Portfolio Value
            </div>
            <div className="flex items-baseline gap-2 mb-1" data-id="element-1715">
              <h1 className="text-4xl font-light text-text-primary tracking-tight font-numbers" data-id="element-1716">
                {totalPortfolioValue.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}
              </h1>
              <span className="text-lg text-text-secondary font-normal" data-id="element-1717">
                USD
              </span>
            </div>
            <span className={`flex items-center gap-1 text-sm font-medium ${changeData.amount >= 0 ? 'text-success' : 'text-danger'}`} data-id="element-1718">
              <TrendingUp size={14} className={changeData.amount < 0 ? 'rotate-180' : ''} data-id="element-1719" />
              {changeData.amount >= 0 ? '+' : ''}$
              {Math.abs(changeData.amount).toLocaleString()} (
              {changeData.percent >= 0 ? '+' : ''}
              {changeData.percent.toFixed(2)}%){' '}
              <span className="text-text-secondary" data-id="element-1720">{timeRange}</span>
            </span>
          </div>

          {/* Timeframe Selector - Top Right */}
          <div className="w-full md:w-auto overflow-x-auto pb-2 md:pb-0 -mx-2 px-2 md:mx-0 md:px-0" data-id="element-1721">
            <div className="flex items-center gap-2 bg-background-elevated p-1 rounded-lg w-max md:w-auto" data-id="element-1722">
              {ranges.map(range => <button key={range} onClick={() => setTimeRange(range)} className={`px-3 py-1 text-xs font-medium rounded-md transition-all border ${timeRange === range ? 'bg-background-elevated text-text-primary border-border-default' : 'text-text-secondary border-transparent hover:text-text-primary hover:bg-white/5'}`} data-id="element-1723">
                  {range}
                </button>)}
            </div>
          </div>
        </div>

        {/* Chart */}
        <div className="relative w-full h-[200px] md:h-[300px] mb-6" data-id="element-1724">
          <ResponsiveContainer width="100%" height="100%" data-id="element-1725">
            <AreaChart data={data} margin={{
            left: 10,
            right: 10,
            top: 10,
            bottom: 10
          }} data-id="element-1726">
              <defs data-id="element-1727">
                <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1" data-id="element-1728">
                  <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.2} data-id="element-1729" />
                  <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0} data-id="element-1730" />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#2B3139" vertical={false} opacity={0.3} data-id="element-1731" />
              <XAxis dataKey="time" axisLine={false} tickLine={false} tick={{
              fill: '#848E9C',
              fontSize: 11
            }} dy={0} interval="preserveEnd" minTickGap={50} data-id="element-1732" />
              <YAxis hide domain={['dataMin - 100', 'dataMax + 100']} data-id="element-1733" />
              <Tooltip content={<CustomTooltip timeRange={timeRange} data-id="element-1735" />} wrapperStyle={{
              zIndex: 1000
            }} cursor={{
              stroke: '#8B5CF6',
              strokeWidth: 1,
              strokeDasharray: '4 4'
            }} data-id="element-1734" />
              <Area type="monotoneX" dataKey="value" stroke="#8B5CF6" strokeWidth={1.5} fillOpacity={1} fill="url(#colorValue)" animationDuration={1500} animationBegin={0} animationEasing="ease-out" isAnimationActive={true} data-id="element-1736" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row items-center gap-2 pt-4 border-t border-border-subtle" data-id="element-1737">
          <button className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 text-sm text-text-secondary hover:text-text-primary hover:bg-white/5 border border-border-subtle hover:border-border-default rounded-full transition-colors" data-id="element-1738">
            <Download size={16} data-id="element-1739" />
            Deposit
          </button>
          <button className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 text-sm text-text-secondary hover:text-text-primary hover:bg-white/5 border border-border-subtle hover:border-border-default rounded-full transition-colors" data-id="element-1740">
            <Upload size={16} data-id="element-1741" />
            Withdraw
          </button>
          <button className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 text-sm text-text-secondary hover:text-text-primary hover:bg-white/5 border border-border-subtle hover:border-border-default rounded-full transition-colors" data-id="element-1742">
            <ArrowLeftRight size={16} data-id="element-1743" />
            Transfer
          </button>
        </div>
      </div>

      {/* Account Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-1744">
        {accounts.map(account => <button key={account.id} onClick={() => handleAccountClick(account.id)} className="glass-border glass-border-hover rounded-[20px] p-6 transition-all cursor-pointer text-left" data-id="element-1745">
            <div className="flex items-start justify-between mb-4" data-id="element-1746">
              <div className="flex items-center gap-3" data-id="element-1747">
                <div className={`p-2 rounded-lg ${account.color}`} data-id="element-1748">
                  {account.icon}
                </div>
                <div data-id="element-1749">
                  <h3 className="text-lg font-semibold text-text-primary" data-id="element-1750">
                    {account.name}
                  </h3>
                  {account.subAccounts && <p className="text-xs text-text-secondary" data-id="element-1751">
                      {account.subAccounts.length} sub-accounts
                    </p>}
                </div>
              </div>
            </div>

            <div className="space-y-3" data-id="element-1752">
              <div data-id="element-1753">
                <div className="text-xs text-text-secondary mb-1" data-id="element-1754">
                  Total Value
                </div>
                <div className="text-2xl font-numbers text-text-primary" data-id="element-1755">
                  {account.totalValue.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}{' '}
                  <span className="text-sm text-text-secondary" data-id="element-1756">USD</span>
                </div>
              </div>

              {account.subAccounts ? <div className="grid grid-cols-2 gap-3 pt-3 border-t border-border-subtle" data-id="element-1757">
                  {account.subAccounts.map(sub => <div key={sub.name} data-id="element-1758">
                      <div className="text-xs text-text-secondary mb-1" data-id="element-1759">
                        {sub.name}
                      </div>
                      <div className="text-sm font-numbers text-text-primary" data-id="element-1760">
                        {sub.balance.toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}
                      </div>
                      <div className={`text-xs font-numbers ${sub.upnl >= 0 ? 'text-success' : 'text-danger'}`} data-id="element-1761">
                        {sub.upnl >= 0 ? '+' : ''}
                        {sub.upnl.toFixed(2)}
                      </div>
                    </div>)}
                </div> : <div className="grid grid-cols-2 gap-3 pt-3 border-t border-border-subtle" data-id="element-1762">
                  {account.balance !== undefined && <div data-id="element-1763">
                      <div className="text-xs text-text-secondary mb-1" data-id="element-1764">
                        Balance
                      </div>
                      <div className="text-sm font-numbers text-text-primary" data-id="element-1765">
                        {account.balance.toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}
                      </div>
                    </div>}
                  {account.upnl !== undefined && <div data-id="element-1766">
                      <div className="text-xs text-text-secondary mb-1" data-id="element-1767">
                        UP&L
                      </div>
                      <div className={`text-sm font-numbers ${account.upnl >= 0 ? 'text-success' : 'text-danger'}`} data-id="element-1768">
                        {account.upnl >= 0 ? '+' : ''}
                        {account.upnl.toFixed(2)}
                      </div>
                    </div>}
                  {account.rewards !== undefined && <div data-id="element-1769">
                      <div className="text-xs text-text-secondary mb-1" data-id="element-1770">
                        Rewards (1w)
                      </div>
                      <div className="text-sm font-numbers text-success" data-id="element-1771">
                        +{account.rewards.toFixed(2)}
                      </div>
                    </div>}
                </div>}
            </div>
          </button>)}
      </div>
    </div>;
}